/**
 * Azure Storage connection-string / URL parser for the paste field (spec §4.1).
 *
 * A thin adapter over the pure core parser (`src/core/engines/
 * azureStorageConnectionString.ts`) that turns its typed result into form
 * values. It mirrors, for the renderer's `ConnectionFormValues`, what
 * `electron/connection-managers/addConnectionAzureStorage.ts` does for
 * `AddConnectionInput` — the two targets are different shapes, the mapping
 * rules are the same and must stay in step.
 *
 * The pasted string itself is never stored, logged or tracked; the secret goes
 * to `password` only and never appears in a status line.
 */
import type { ConnectionFormValues } from '../../components/connectionFormSchema';
import type { AzureStorageEndpoints } from '@/types/azureStorage';
import { storageServiceOf } from '@/types/azureStorage';
import {
  parseAzureStorageConnectionString,
  type AzureStorageParseResult,
} from '@/core/engines/azureStorageConnectionString';
import { DEFAULT_ENDPOINT_SUFFIX, isLoopbackHost, serviceEndpointFor } from '@/core/engines/azureStorageUrl';
import type { ConnectionStringParser, ParserMatch } from './types';
import { redactSecretText } from '@/types/safeConnectionDisplay';
import { sharePathFormValues } from '../../components/azureStorageSharePath';

function match(values: Partial<ConnectionFormValues>, matchedFields: string[], unmatchedKeys: string[] = []): ParserMatch {
  return {
    format: 'azure-storage',
    engine: 'azurestorage',
    values: { engine: 'azurestorage', port: '0', ...values },
    matchedFields,
    unmatchedKeys,
  };
}

/** A non-default `EndpointSuffix` has no `Connection` slot (ruling O4) — pin it as explicit endpoints. */
function endpointsForSuffix(accountName: string, endpoints: AzureStorageEndpoints, suffix: string | undefined): AzureStorageEndpoints {
  const trimmed = suffix?.trim();
  if (!trimmed || trimmed === DEFAULT_ENDPOINT_SUFFIX) return endpoints;
  const account = { accountName, endpoints, suffix: trimmed };
  return {
    blob: serviceEndpointFor(account, 'blob'),
    table: serviceEndpointFor(account, 'table'),
    queue: serviceEndpointFor(account, 'queue'),
    file: serviceEndpointFor(account, 'file'),
    dfs: serviceEndpointFor(account, 'dfs'),
  };
}

function hasEndpoints(endpoints: AzureStorageEndpoints): boolean {
  return Object.values(endpoints).some((value) => typeof value === 'string' && value.length > 0);
}

/**
 * A path-style (loopback) URL is indistinguishable from any other local service
 * URL — `http://127.0.0.1:8080/whatever` parses as "account `whatever`" — so a
 * loopback URL is only ours when it carries a SAS signature. Host-style URLs
 * had to name a storage service label (`*.blob.` / `*.table.` / …) to parse at
 * all, so those are claimed as they come.
 */
function claimsUrlInput(raw: string): boolean {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }
  return !isLoopbackHost(url.hostname) || url.searchParams.has('sig');
}

/** S8: a Data Lake (dfs host) URL still dials the BLOB host for every Blob-API call; the dfs host is kept as the dfs endpoint. */
const DFS_HOST = /^(https?:\/\/[^/.]+)\.dfs\./iu;

function fromResourceUrl(parsed: Extract<AzureStorageParseResult, { kind: 'sas-url' | 'public-url' }>): ParserMatch {
  const service = storageServiceOf(parsed.resource);
  const dfsHost = service === 'blob' && DFS_HOST.test(parsed.endpoint);
  const server = dfsHost ? parsed.endpoint.replace(DFS_HOST, '$1.blob.') : parsed.endpoint;
  const values: Partial<ConnectionFormValues> = {
    server,
    authentication: parsed.kind === 'sas-url' ? 'Shared Access Signature' : 'Anonymous',
    // S7-10: an ambiguous share path is the file candidate until the form's File / Folder choice is made.
    ...sharePathFormValues(parsed),
    azureStorageEndpoints: dfsHost ? { blob: server, dfs: parsed.endpoint } : service ? { [service]: parsed.endpoint } : undefined,
    azureStorageServices: service ? [service] : undefined,
    azureStorageEmulator: false,
  };
  const fields = ['resource', 'endpoints'];
  if (parsed.kind === 'sas-url') {
    values.password = parsed.sas;
    fields.splice(1, 0, 'SAS');
  }
  return match(values, fields);
}

export const parseAzureStorage: ConnectionStringParser = (input) => {
  const trimmed = input.trim();
  if (/^https?:\/\//i.test(trimmed) && !claimsUrlInput(trimmed)) return null;

  const parsed = parseAzureStorageConnectionString(trimmed);
  if (!parsed) return null;

  switch (parsed.kind) {
    case 'invalid':
      // Recognisably ours but unusable (a duplicate key, a FileEndpoint-only Azurite string …): the reason is the match's
      // `problem` (secret-free), as Kusto states its refusals, so the paste fills NOTHING — not even the engine's default
      // authentication over the SAS or Entra draft it lands on (`parseResultPatch`).
      return {
        format: 'azure-storage',
        engine: 'azurestorage',
        values: {},
        matchedFields: [],
        unmatchedKeys: [],
        problem: redactSecretText(parsed.reason),
      };

    case 'account-key': {
      const endpoints = endpointsForSuffix(parsed.accountName, parsed.endpoints, parsed.suffix);
      const fields = ['account', 'key'];
      if (hasEndpoints(endpoints)) fields.push('endpoints');
      return match(
        {
          server: parsed.accountName,
          authentication: 'Account Key',
          password: parsed.accountKey,
          azureStorageEndpoints: hasEndpoints(endpoints) ? endpoints : undefined,
          azureStorageScope: { kind: 'account' },
          azureStorageEmulator: false,
        },
        fields
      );
    }

    case 'sas': {
      const firstEndpoint = Object.values(parsed.endpoints).find((value): value is string => typeof value === 'string');
      const fields = parsed.accountName ? ['account', 'SAS'] : ['SAS'];
      if (hasEndpoints(parsed.endpoints)) fields.push('endpoints');
      return match(
        {
          server: parsed.accountName ?? firstEndpoint ?? '',
          authentication: 'Shared Access Signature',
          password: parsed.sas,
          azureStorageEndpoints: hasEndpoints(parsed.endpoints) ? parsed.endpoints : undefined,
          azureStorageScope: { kind: 'account' },
          azureStorageEmulator: false,
        },
        fields
      );
    }

    case 'account-sas-url':
      return match({
        server: parsed.accountName ?? parsed.endpoint,
        authentication: 'Shared Access Signature', password: parsed.sas,
        azureStorageEndpoints: parsed.endpoints,
        azureStorageScope: { kind: 'account' }, azureStorageEmulator: false,
      }, ['account', 'SAS', 'endpoints']);

    case 'sas-query':
      return match({ server: '', authentication: 'Shared Access Signature', password: parsed.sas, azureStorageScope: { kind: 'account' }, azureStorageEmulator: false }, ['SAS']);

    case 'development-storage':
      // `UseDevelopmentStorage=true`, or the full string with Azurite's endpoints (the shared rule in
      // `azureStorageUrl.ts`): the Emulator (Azurite) preset with the string's account, key, endpoints and
      // services. A key the emulator does not use (a loopback FileEndpoint) is listed as not used, never dropped silently.
      return match(
        {
          server: parsed.accountName,
          authentication: 'Account Key',
          password: parsed.accountKey,
          azureStorageEndpoints: parsed.endpoints,
          azureStorageServices: [...parsed.services],
          azureStorageScope: { kind: 'account' },
          azureStorageEmulator: true,
        },
        ['emulator', 'account', 'key', 'endpoints'],
        parsed.unusedKeys
      );

    case 'sas-url':
    case 'public-url':
      return fromResourceUrl(parsed);
  }
};
